// src/components/ServiceCategoriesTable.tsx
import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  fetchServiceCategories,
  createServiceCategory,
  suggestCategoryAliases,
  updateServiceCategory,
  deleteServiceCategory,
  ServiceCategory,
} from "../api/serviceCategories";
import { useAuth } from "../context/AuthContext";

const ServiceCategoriesTable: React.FC = () => {
  const { businessId } = useAuth();
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceCategory | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<ServiceCategory | null>(null);

  const [name, setName] = useState("");
  const [sortOrder, setSortOrder] = useState<number | "">("");
  const [aliasesInput, setAliasesInput] = useState("");
  const [suggestedAliases, setSuggestedAliases] = useState<string[]>([]);
  const [suggestingAliases, setSuggestingAliases] = useState(false);
  const [bulkAliasesOpen, setBulkAliasesOpen] = useState(false);
  const [bulkAliasesRunning, setBulkAliasesRunning] = useState(false);
  const [bulkAliasesProgress, setBulkAliasesProgress] = useState({
    completed: 0,
    failed: 0,
    total: 0,
  });
  const [bulkAliasesSummary, setBulkAliasesSummary] = useState<string | null>(null);

  const parseAliases = (raw: string): string[] => {
    const seen = new Set<string>();
    return raw
      .split(",")
      .map((part) => part.trim().replace(/\s+/g, " "))
      .filter((part) => {
        if (!part) return false;
        const key = part.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  // load categories
  useEffect(() => {
    if (!businessId) return;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchServiceCategories();
        setCategories(data);
      } catch (err) {
        console.error("🚨 Failed to fetch categories:", err);
        setError("Failed to load categories.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [businessId]);

  const openAddDialog = () => {
    setEditing(null);
    setName("");
    setSortOrder(categories.length ? categories[categories.length - 1].sort_order + 10 : 10);
    setAliasesInput("");
    setSuggestedAliases([]);
    setEditOpen(true);
  };

  const openEditDialog = (cat: ServiceCategory) => {
    setEditing(cat);
    setName(cat.name);
    setSortOrder(cat.sort_order);
    setAliasesInput((cat.aliases || []).join(", "));
    setSuggestedAliases([]);
    setEditOpen(true);
  };

  const handleSuggestAliases = async () => {
    if (!name.trim()) {
      alert("Enter a category name first.");
      return;
    }

    try {
      setSuggestingAliases(true);
      const suggestions = await suggestCategoryAliases({
        service_category_id: editing?.service_category_id,
        name: name.trim(),
        aliases: parseAliases(aliasesInput),
      });
      setSuggestedAliases(suggestions);
    } catch (err) {
      console.error("🚨 Failed to suggest aliases:", err);
      alert("Failed to generate alias suggestions. See console for details.");
    } finally {
      setSuggestingAliases(false);
    }
  };

  const handleAddSuggestedAlias = (alias: string) => {
    const merged = parseAliases(
      [aliasesInput, alias]
        .filter(Boolean)
        .join(", ")
    );
    setAliasesInput(merged.join(", "));
    setSuggestedAliases((prev) => prev.filter((item) => item.toLowerCase() !== alias.toLowerCase()));
  };

  const openBulkAliasesDialog = () => {
    setBulkAliasesProgress({ completed: 0, failed: 0, total: categories.length });
    setBulkAliasesSummary(null);
    setBulkAliasesOpen(true);
  };

  const handleBulkAliasGeneration = async () => {
    const categorySnapshot = [...categories];

    setBulkAliasesRunning(true);
    setBulkAliasesSummary(null);
    setBulkAliasesProgress({
      completed: 0,
      failed: 0,
      total: categorySnapshot.length,
    });

    let failed = 0;

    for (const category of categorySnapshot) {
      try {
        const aliases = await suggestCategoryAliases({
          service_category_id: category.service_category_id,
          name: category.name,
          aliases: [],
        });

        if (!aliases.length) {
          throw new Error("No aliases generated");
        }

        const updated = await updateServiceCategory(category.service_category_id, {
          name: category.name,
          sort_order: category.sort_order,
          aliases,
        });

        setCategories((prev) =>
          prev.map((item) =>
            item.service_category_id === category.service_category_id
              ? updated
              : item
          )
        );
      } catch (err) {
        failed += 1;
        console.error("🚨 Failed to bulk-generate aliases:", err);
      } finally {
        setBulkAliasesProgress((prev) => ({
          completed: prev.completed + 1,
          failed,
          total: categorySnapshot.length,
        }));
      }
    }

    setBulkAliasesRunning(false);
    setBulkAliasesSummary(
      `Updated aliases for ${categorySnapshot.length - failed} of ${categorySnapshot.length} categories.`
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      alert("Category name is required");
      return;
    }

    const parsedSort = sortOrder === "" ? 0 : Number(sortOrder) || 0;
    const aliases = parseAliases(aliasesInput);

    try {
      if (editing) {
        const updated = await updateServiceCategory(editing.service_category_id, {
          name: name.trim(),
          sort_order: parsedSort,
          aliases,
        });
        setCategories((prev) =>
          prev.map((c) =>
            c.service_category_id === editing.service_category_id ? updated : c
          )
        );
      } else {
        const created = await createServiceCategory({
          name: name.trim(),
          sort_order: parsedSort,
          aliases,
        });
        setCategories((prev) => [...prev, created]);
      }
      setEditOpen(false);
    } catch (err) {
      console.error("🚨 Failed to save category:", err);
      alert("Failed to save category. See console for details.");
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteServiceCategory(toDelete.service_category_id);
      setCategories((prev) =>
        prev.filter((c) => c.service_category_id !== toDelete.service_category_id)
      );
    } catch (err) {
      console.error("🚨 Failed to delete category:", err);
      alert(
        "Could not delete category. It may still be used by services. Remove or reassign those services first."
      );
    } finally {
      setDeleteOpen(false);
      setToDelete(null);
    }
  };

  if (loading) {
    return <Typography>Loading categories...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Service Categories
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Categories group services on your booking page and control their display order.
        </Typography>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          <Button variant="contained" onClick={openAddDialog}>
            Add Category
          </Button>
          <Button
            variant="outlined"
            onClick={openBulkAliasesDialog}
            disabled={categories.length === 0 || bulkAliasesRunning}
          >
            Generate aliases
          </Button>
        </Box>

        {categories.length === 0 ? (
          <Typography color="text.secondary">
            No categories yet. Add your first category.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Sort Order</TableCell>
                <TableCell>Aliases</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((cat) => (
                <TableRow key={cat.service_category_id} hover>
                  <TableCell>{cat.name}</TableCell>
                  <TableCell>{cat.sort_order}</TableCell>
                  <TableCell>{cat.aliases?.length || 0}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEditDialog(cat)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setToDelete(cat);
                          setDeleteOpen(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Add/Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth>
        <DialogTitle>
          {editing ? "Edit Category" : "Add Category"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Sort Order (optional)"
            fullWidth
            type="number"
            value={sortOrder}
            onChange={(e) =>
              setSortOrder(e.target.value === "" ? "" : Number(e.target.value))
            }
            helperText="Lower numbers appear first on the booking page."
          />
          <Box sx={{ mt: 2 }}>
            <TextField
              margin="dense"
              label="Aliases"
              fullWidth
              value={aliasesInput}
              onChange={(e) => setAliasesInput(e.target.value)}
              helperText="Comma-separated spoken variants, for example brows, eyebrow services, brow lamination."
            />
          </Box>
          <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Button onClick={handleSuggestAliases} disabled={suggestingAliases || !name.trim()}>
              {suggestingAliases ? "Suggesting..." : "Suggest aliases"}
            </Button>
            {suggestedAliases.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Click a suggestion to add it to the alias list.
              </Typography>
            )}
          </Box>
          {suggestedAliases.length > 0 && (
            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
              {suggestedAliases.map((alias) => (
                <Button
                  key={alias}
                  size="small"
                  variant="outlined"
                  onClick={() => handleAddSuggestedAlias(alias)}
                >
                  {alias}
                </Button>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkAliasesOpen}
        onClose={() => {
          if (!bulkAliasesRunning) setBulkAliasesOpen(false);
        }}
        fullWidth
      >
        <DialogTitle>Generate aliases for all categories?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will replace the current aliases for every service category.
            Category names and public service descriptions will not change. You can
            review and edit each category afterward.
          </Typography>
          {bulkAliasesRunning && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={
                  bulkAliasesProgress.total
                    ? (bulkAliasesProgress.completed /
                        bulkAliasesProgress.total) *
                      100
                    : 0
                }
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {bulkAliasesProgress.completed} of {bulkAliasesProgress.total} categories processed
                {bulkAliasesProgress.failed
                  ? `, ${bulkAliasesProgress.failed} failed`
                  : ""}
              </Typography>
            </Box>
          )}
          {bulkAliasesSummary && (
            <Alert
              severity={bulkAliasesProgress.failed ? "warning" : "success"}
              sx={{ mb: 1 }}
            >
              {bulkAliasesSummary}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {bulkAliasesSummary ? (
            <Button onClick={() => setBulkAliasesOpen(false)} color="primary">
              Close
            </Button>
          ) : (
            <>
              <Button
                onClick={() => setBulkAliasesOpen(false)}
                disabled={bulkAliasesRunning}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkAliasGeneration}
                color="primary"
                disabled={bulkAliasesRunning || categories.length === 0}
              >
                {bulkAliasesRunning ? "Generating..." : "Generate aliases"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        fullWidth
      >
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{toDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ServiceCategoriesTable;
